import { Injectable, effect } from '@angular/core'
import { firstValueFrom } from 'rxjs'
import {
  ProgramsService,
  ZonesService,
  ListZone200Response,
} from 'src/api-cyclist'
import { StationWrapperService } from '../api-wrappers/stations.service'
import { NewsStateService } from '../api-wrappers/news.service'
import { ForfaitsWrapperService } from 'src/app/services/api-wrappers/forfaits.service'
import { BookingWrapperService } from 'src/app/services/api-wrappers/booking.service'
import { AbonnementWrapperService } from 'src/app/services/api-wrappers/abonnement.service'
import { KYCWrapperService } from '../api-wrappers/kyc.service'
import { PersonnalisationService } from '../utils/personnalisation.service'
import { StorageService } from '../utils/storage.service'
import { LanguageService } from '../utils/language.service'
import { environment } from 'src/environments/environment'
import { ErrorHandlerService } from '../utils/error-handler.service'
import { AuthService } from '../external/auth.service'

export interface LaunchOptions {
  skipIfDataExists?: boolean
  forceRefreshStations?: boolean
}

/**
 * `pending` : personne n'a encore rien chargé.
 * `loading` : un chargement est en cours.
 * Seul l'écran de lancement charge ces données ; les pages qui le contournent
 * n'en déclenchent aucun.
 */
export type LaunchDataState =
  | { state: 'pending' }
  | { state: 'loading' }
  | { state: 'ready' }

enum LoadState {
  UNINITIALIZED = 'UNINITIALIZED',
  INITIALIZED = 'INITIALIZED',
}

@Injectable({
  providedIn: 'root',
})
export class LaunchService {
  private loadState = LoadState.UNINITIALIZED
  private launchDataState: LaunchDataState = { state: 'pending' }
  /**
   * Chargement en cours. Deux passages par l'écran de lancement attendent le
   * même, plutôt que d'en lancer un second en parallèle.
   */
  private pendingLoad: Promise<void> | null = null
  /**
   * Vrai une fois la séquence de démarrage jouée dans ce processus — ou
   * délibérément sautée, quand l'application est entrée par une page qui
   * contourne l'écran de lancement. L'écran est aussi traversé en cours de
   * session, où il ne doit que router et charger : ni demande de suivi, ni
   * recherche de mise à jour.
   */
  private launchSequenceRun = false

  hasRunLaunchSequence(): boolean {
    return this.launchSequenceRun
  }

  markLaunchSequenceRun(): void {
    this.launchSequenceRun = true
  }

  constructor(
    private programsService: ProgramsService,
    private zonesService: ZonesService,
    private stationWrapperService: StationWrapperService,
    private newsStateService: NewsStateService,
    private forfaitService: ForfaitsWrapperService,
    private bookingWrapperService: BookingWrapperService,
    private abonnementWrapperService: AbonnementWrapperService,
    private kycService: KYCWrapperService,
    private personnalisationService: PersonnalisationService,
    private storageService: StorageService,
    private languageService: LanguageService,
    private errorHandler: ErrorHandlerService,
    private authService: AuthService
  ) {
    // Subscribe to language changes and reload program when language changes
    effect(() => {
      const currentLang = this.languageService.currentLanguage()

      // Skip the initial load to avoid loading program twice on app startup
      if (this.loadState === LoadState.UNINITIALIZED) {
        this.loadState = LoadState.INITIALIZED
        return
      }

      // Reload program when language changes
      this.loadProgram().catch((error) => {
        console.error('Error reloading program after language change:', error)
      })
    })
  }

  getLaunchDataState(): LaunchDataState {
    return this.launchDataState
  }

  /**
   * Chargement pour une entrée qui contourne l'écran de lancement et a
   * néanmoins besoin des données - un retour Stripe atterrit sur une page qui
   * lit le programme et les forfaits. Silencieux : rien n'attend cet appel,
   * la page s'affiche immédiatement et se remplit quand les données arrivent.
   *
   * L'attente de la session est le point important : lancer ce chargement
   * avant qu'elle soit tranchée le faisait courir contre l'établissement de
   * l'authentification, et les routes protégées répondaient 403.
   */
  async loadEssentialDataInBackground(): Promise<void> {
    try {
      await this.authService.whenAuthResolved()
      await this.loadEssentialData({ skipIfDataExists: true })
    } catch (error) {
      console.error(
        'Chargement des données essentielles en arrière-plan échoué :',
        error
      )
    }
  }

  /**
   * Load all essential app data with retry logic
   */
  async loadEssentialData(options: LaunchOptions = {}): Promise<void> {
    if (!this.pendingLoad) {
      this.pendingLoad = this.runEssentialDataLoad(options).finally(() => {
        this.pendingLoad = null
      })
    }

    return this.pendingLoad
  }

  private async runEssentialDataLoad(options: LaunchOptions): Promise<void> {
    this.launchDataState = { state: 'loading' }

    // Ensure wrapper services are instantiated early (auth-driven effects, rentingMode resolution).
    void this.bookingWrapperService
    void this.abonnementWrapperService

    // Check if essential data already exists in storage
    if (options.skipIfDataExists) {
      const existingProgram = await this.storageService.get('program')
      const existingZones = await this.storageService.get('zones')
      const existingForfaits = await this.storageService.get('forfaits')
      const existingStations = await this.storageService.get('stations')

      if (
        existingProgram &&
        existingZones &&
        existingForfaits &&
        existingStations
      ) {
        // Still set colors and title from existing program
        this.personnalisationService.setColors()
        this.personnalisationService.setTitle()
        this.personnalisationService.setLogo(existingProgram.config?.logo)
        this.kycService.setKycDocumentTypes()
        this.launchDataState = { state: 'ready' }
        return
      }
    }

    const retryOperation = async (
      operation: () => Promise<any>,
      name: string,
      maxRetries = 3
    ) => {
      for (let i = 0; i < maxRetries; i++) {
        try {
          return await operation()
        } catch (error) {
          if (i === maxRetries - 1) {
            console.error(`Final retry failed for ${name}:`, error)
            throw error
          }
          console.warn(
            `Retry ${i + 1}/${maxRetries} for ${name} after error:`,
            error
          )
          await new Promise((resolve) => setTimeout(resolve, 1000 * (i + 1))) // Exponential backoff
        }
      }
    }

    try {
      const operations = []

      // Check what needs to be loaded
      const existingProgram = await this.storageService.get('program')
      const existingZones = await this.storageService.get('zones')

      // Only load what's missing or requested
      if (!existingProgram || !options.skipIfDataExists) {
        operations.push(retryOperation(() => this.loadProgram(), 'loadProgram'))
      }

      // Always refresh stations (they change frequently)
      operations.push(
        retryOperation(
          () => this.loadStations(options.forceRefreshStations),
          'loadStations'
        )
      )

      if (!existingZones || !options.skipIfDataExists) {
        operations.push(retryOperation(() => this.loadZones(), 'loadZones'))
      }

      // Always refresh news and forfaits
      operations.push(
        retryOperation(
          () => this.newsStateService.retrieveNews(),
          'retrieveNews'
        ),
        retryOperation(() => this.forfaitService.loadForfaits(), 'loadForfaits')
      )

      await Promise.all(operations)
    } catch (error) {
      this.errorHandler.handleError(error)
      console.error('❌ LaunchService: Error loading essential data:', error)
      // Don't throw here, some data might be optional
    } finally {
      this.launchDataState = { state: 'ready' }
    }
  }

  /**
   * Load program configuration
   */
  public async loadProgram(): Promise<void> {
    const program: any = await firstValueFrom(
      this.programsService.retrieveProgram({
        program: environment.program,
        expand: ['config'],
        language: this.languageService.currentLanguage(),
      })
    )

    if (program.config?.kyc_document_types) {
      this.kycService.kyc_document_types.set(program.config.kyc_document_types)
    }

    await this.storageService.set('program', program)
    this.personnalisationService.setColors()
    this.personnalisationService.setTitle()
    this.personnalisationService.setLogo(program.config?.logo)
    this.kycService.setKycDocumentTypes()
  }

  /**
   * Load stations data
   */
  private async loadStations(forceRefresh: boolean = true): Promise<void> {
    await this.stationWrapperService.refreshStations(forceRefresh)
  }

  /**
   * Load zones data
   */
  private async loadZones(): Promise<void> {
    const response: ListZone200Response = await firstValueFrom(
      this.zonesService.listZone({
        program: environment.program,
        status: 'open',
        liveMode: environment.production,
        limit: 100,
      })
    )

    const zonesToStore =
      'zones' in response ? (response as any).zones : response
    await this.storageService.set('zones', zonesToStore)
  }
}
